import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { publicLink } from "@/lib/landerDomain";
import { X, TestTube, Eye, EyeSlash, Shuffle, Copy, CheckCircle, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { SendTestLeadButton } from "@/components/SendTestLeadButton";

/**
 * Iter-70 — "Seed test accounts" dialog.
 *
 * Super_admin one-click bootstrapper: creates (or password-rotates)
 * four canary users — one per major role (employee, media_buyer,
 * buyer, vendor) — sharing a single password the admin specifies.
 * The buyer + vendor accounts get auto-linked to the seeded
 * LeadFlow Test Buyer client + Grow My Firm Online affiliate so they
 * see real data on their dashboards.
 *
 * After success the dialog renders a receipt panel with:
 *   - The 4 email addresses + resolved role + linked org
 *   - The shared password (revealable)
 *   - A "Copy all credentials" button that dumps the whole set to
 *     the clipboard as `email : password` lines — perfect for
 *     pasting into a password manager.
 *
 * Test accounts are stamped `is_test_account=true` on the backend
 * (renders a "TEST" chip in the /employees list) and skip forced
 * password rotation so the admin's chosen value keeps working
 * across repeated sessions.
 */

const ROLE_LABEL = {
  employee: "Employee",
  media_buyer: "Media Buyer",
  buyer: "Buyer (client-side)",
  vendor: "Affiliate login",
};

export default function SeedTestAccountsDialog({ onClose, onSaved }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [warnings, setWarnings] = useState([]);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= 8 && password === confirm && !busy && !receipt;

  const generate = () => {
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*";
    const N = 16;
    let vals;
    try {
      const src = (typeof window !== "undefined" && (window.crypto || window.msCrypto)) || null;
      if (src && typeof src.getRandomValues === "function") {
        const bytes = new Uint8Array(N);
        src.getRandomValues(bytes);
        vals = Array.from(bytes, (b) => alphabet[b % alphabet.length]);
      }
    } catch { /* fall through */ }
    if (!vals || vals.some((c) => c === undefined)) {
      vals = Array.from({ length: N },
        () => alphabet[Math.floor(Math.random() * alphabet.length)]);
    }
    const val = vals.join("");
    setPassword(val); setConfirm(val); setReveal(true);
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post("/employees/seed-test-accounts", {
        password,
      });
      setReceipt({ password, accounts: data.accounts || [], profile: data.test_profile, vendor: data.test_vendor, delivery: data.test_delivery });
      setWarnings(data.warnings || []);
      toast.success(`Seeded ${data.count} test accounts`);
      onSaved?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to seed test accounts");
    } finally { setBusy(false); }
  };

  const copyAll = async () => {
    if (!receipt) return;
    const lines = receipt.accounts.map(
      (a) => `${a.email} : ${receipt.password}   (${ROLE_LABEL[a.role] || a.role})`,
    );
    lines.unshift(`# LeadFlow test accounts — shared password: ${receipt.password}`);
    lines.push(`# Log in at: ${window.location.origin}/login`);
    (receipt.profile?.links || []).forEach((l) => lines.push(`# ${l.title}: ${publicLink(l.path)}`));
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("All 4 credentials copied to clipboard");
    } catch { toast.error("Clipboard blocked"); }
  };

  const done = () => {
    setPassword(""); setConfirm(""); setReceipt(null); setWarnings([]);
    onClose?.();
  };

  const backdropClick = (e) => { if (e.target === e.currentTarget) done(); };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
      data-testid="seed-test-dialog"
      onClick={backdropClick}
    >
      <div className="bg-white border-4 border-black w-full max-w-lg brut-shadow-sm">
        <div className="flex items-start justify-between p-4 border-b-2 border-black">
          <div className="flex items-center gap-2">
            <TestTube size={16} weight="bold" />
            <div>
              <div className="label-mono">Super_admin action</div>
              <div className="font-chivo font-black text-lg uppercase tracking-tighter leading-none">
                Seed 4 test accounts
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={done}
            data-testid="seed-test-close"
            className="border border-black p-1 hover:bg-black hover:text-white"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {!receipt && (
          <form onSubmit={submit}>
            <div className="p-4 space-y-3">
              <div className="border-2 border-black bg-yellow-50 p-3 text-[11px] leading-relaxed">
                Creates one canary user per role — <b>employee</b>, <b>media_buyer</b>,
                <b> buyer</b>, and <b>affiliate</b> — all at <span className="font-mono">test-*@jellisenterprise.com</span>.
                Buyer is linked to <b>LeadFlow Test Buyer</b> and a published funnel profile, <b>Test Buyer Firm</b>.
                The affiliate login is linked to a dedicated <b>LeadFlow Test Vendor</b> org, approved for the 3 shared funnels and the test buyer&apos;s links.
                Media buyer can see the 3 shared funnels.
                Running this again just rotates the password on the same four accounts —
                it never creates duplicates.
              </div>

              <div>
                <label className="label-mono">Shared password</label>
                <div className="flex items-stretch gap-2 mt-1">
                  <input
                    type={reveal ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="seed-test-password"
                    autoFocus
                    autoComplete="new-password"
                    className={`flex-1 border ${tooShort ? "border-red-600" : "border-black"} px-3 py-2 font-mono text-[12px]`}
                    placeholder="Minimum 8 characters — same value goes on all 4 accounts"
                  />
                  <button
                    type="button"
                    onClick={() => setReveal((r) => !r)}
                    data-testid="seed-test-reveal"
                    className="border border-black px-2 hover:bg-black hover:text-white"
                    title={reveal ? "Hide" : "Reveal"}
                  >
                    {reveal ? <EyeSlash size={12} weight="bold" /> : <Eye size={12} weight="bold" />}
                  </button>
                  <button
                    type="button"
                    onClick={generate}
                    data-testid="seed-test-generate"
                    className="border border-black px-2 hover:bg-black hover:text-white"
                    title="Generate a random 16-char password"
                  >
                    <Shuffle size={12} weight="bold" />
                  </button>
                </div>
                {tooShort && (
                  <div className="text-[10px] text-red-700 mt-1" data-testid="seed-test-too-short">
                    Password must be at least 8 characters.
                  </div>
                )}
              </div>

              <div>
                <label className="label-mono">Confirm password</label>
                <input
                  type={reveal ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="seed-test-confirm"
                  autoComplete="new-password"
                  className={`w-full border ${mismatch ? "border-red-600" : "border-black"} px-3 py-2 mt-1 font-mono text-[12px]`}
                />
                {mismatch && (
                  <div className="text-[10px] text-red-700 mt-1" data-testid="seed-test-mismatch">
                    Passwords don&apos;t match.
                  </div>
                )}
              </div>
            </div>

            <div className="border-t-2 border-black px-4 py-3 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={done}
                data-testid="seed-test-cancel"
                className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="seed-test-submit"
                className="border border-black bg-black text-white px-4 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40"
              >
                {busy ? "Seeding…" : "Seed 4 accounts"}
              </button>
            </div>
          </form>
        )}

        {receipt && (
          <div className="p-4 space-y-3" data-testid="seed-test-receipt">
            <div className="border-2 border-emerald-700 bg-emerald-50 p-3 text-xs">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle size={14} weight="fill" className="text-emerald-700" />
                <span className="font-bold uppercase tracking-widest text-emerald-900 text-[10px]">
                  {receipt.accounts.length} accounts ready
                </span>
              </div>
              <div className="leading-relaxed">
                All four log in with the shared password below. They&apos;re
                stamped <span className="font-mono">is_test_account:true</span> so you can
                bulk-purge before production.
              </div>
            </div>

            <div className="border-2 border-black">
              <table className="w-full text-xs" data-testid="seed-test-accounts-table">
                <thead>
                  <tr className="bg-black text-white uppercase tracking-widest text-[10px]">
                    <th className="text-left py-2 px-3">Email</th>
                    <th className="text-left py-2 px-3">Role</th>
                    <th className="text-left py-2 px-3">Linked org</th>
                  </tr>
                </thead>
                <tbody>
                  {receipt.accounts.map((a) => (
                    <tr key={a.email} className="border-b border-zinc-200" data-testid={`seed-test-row-${a.role}`}>
                      <td className="py-2 px-3 font-mono text-[11px]">{a.email}</td>
                      <td className="py-2 px-3">
                        <span className="border border-black bg-zinc-100 px-1.5 py-0.5 text-[9px] uppercase tracking-widest">
                          {ROLE_LABEL[a.role] || a.role}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-[11px] text-zinc-700">
                        {a.linked_org_name || (a.org_type === "internal" ? "—" : <em>none found</em>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <div className="label-mono mb-1">Shared password</div>
              <div className="flex gap-2 items-stretch">
                <input
                  readOnly
                  value={receipt.password}
                  data-testid="seed-test-receipt-password"
                  type={reveal ? "text" : "password"}
                  className="flex-1 border border-black px-3 py-2 font-mono text-[12px] bg-zinc-50"
                />
                <button
                  type="button"
                  onClick={() => setReveal((r) => !r)}
                  className="border border-black px-2 hover:bg-black hover:text-white"
                >
                  {reveal ? <EyeSlash size={12} weight="bold" /> : <Eye size={12} weight="bold" />}
                </button>
              </div>
            </div>

            {receipt.profile && (
              <div className="border-2 border-black p-3 text-[11px]" data-testid="seed-test-profile">
                <div className="label-mono mb-1">Test buyer funnel links (affiliate {receipt.vendor?.public_source_id})</div>
                {receipt.delivery?.inbox && (
                  <div className="text-[10px] mb-1" data-testid="seed-test-delivery">
                    Test leads go to the built-in test inbox + email copies to {receipt.delivery.email_copies.join(", ")}
                  </div>
                )}
                {receipt.profile.links.map((l) => (
                  <div key={l.funnel} className="font-mono text-[10px] break-all" data-testid={`seed-test-profile-link-${l.funnel}`}>
                    {publicLink(l.path)}
                  </div>
                ))}
                {receipt.profile.id && (
                  <div className="mt-2">
                    <SendTestLeadButton url="/admin/global-funnels/test-lead" body={{ buyer_page_id: receipt.profile.id }}
                                        testId="seed-test-lead" showLeadLink />
                  </div>
                )}
              </div>
            )}

            {warnings.length > 0 && (
              <div className="border-2 border-amber-700 bg-amber-50 p-3 text-[11px]" data-testid="seed-test-warnings">
                <div className="flex items-center gap-1 font-bold uppercase tracking-widest text-amber-900 text-[10px] mb-1">
                  <Warning size={12} weight="fill" /> Warnings ({warnings.length})
                </div>
                <ul className="list-disc pl-4 space-y-1 text-amber-900">
                  {warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            <div className="border-t-2 border-black -mx-4 px-4 pt-3 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={copyAll}
                data-testid="seed-test-copy-all"
                className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
              >
                <Copy size={11} weight="bold" /> Copy all creds
              </button>
              <button
                type="button"
                onClick={done}
                data-testid="seed-test-done"
                className="border border-black bg-black text-white px-4 py-1.5 text-[11px] uppercase tracking-widest"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
